import { useEffect, useState } from "react";
import { alertsApi } from "@/api";
import { Bell, AlertTriangle, CheckCircle2, XCircle, MessageSquare, SkipForward, X, Filter } from "lucide-react";

const severityConfig = {
  critical: { label: "CRITICAL", border: "border-l-red-500", badge: "text-red-400 bg-red-400/10 border border-red-400/20", dot: "bg-red-500" },
  high: { label: "HIGH", border: "border-l-orange-500", badge: "text-orange-400 bg-orange-400/10 border border-orange-400/20", dot: "bg-orange-500" },
  medium: { label: "MEDIUM", border: "border-l-amber-500", badge: "text-amber-400 bg-amber-400/10 border border-amber-400/20", dot: "bg-amber-400" },
  low: { label: "LOW", border: "border-l-slate-500", badge: "text-slate-400 bg-slate-400/10 border border-slate-400/20", dot: "bg-slate-400" },
};

const statusConfig = {
  open: "text-red-400",
  acknowledged: "text-amber-400",
  actioned: "text-green-400",
  overridden: "text-orange-400",
  closed: "text-muted-foreground",
};

function AlertCard({ alert, onUpdate }) {
  const [commenting, setCommenting] = useState(false);
  const [comment, setComment] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overriding, setOverriding] = useState(false);
  const cfg = severityConfig[alert.severity] || severityConfig.low;

  const doUpdate = (status, extra = {}) => onUpdate(alert.id, { status, ...extra });

  return (
    <div className={`bg-card border border-border border-l-4 ${cfg.border} rounded-sm p-4 fade-in-up`} data-testid={`alert-card-${alert.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-start gap-2 flex-1">
          <span className={`inline-block w-2 h-2 rounded-full ${cfg.dot} shrink-0 mt-1.5`} />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-bold tracking-wider ${cfg.badge}`}>{cfg.label}</span>
              <span className="text-xs text-muted-foreground font-mono">{alert.reference}</span>
              <span className="text-xs text-muted-foreground">{alert.department}</span>
            </div>
            <h3 className="font-semibold text-foreground text-sm mt-1 font-heading">{alert.title}</h3>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs font-bold uppercase ${statusConfig[alert.status] || "text-muted-foreground"}`}>{alert.status}</span>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{alert.description}</p>

      {/* Why / Recommendation / Actor */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <div className="bg-background/60 rounded-sm p-2 border border-border/50">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-1">Why Risky</div>
          <div className="text-xs text-foreground leading-relaxed">{alert.description?.split(".")[0]}.</div>
        </div>
        <div className="bg-primary/5 rounded-sm p-2 border border-primary/20">
          <div className="text-xs uppercase tracking-widest text-primary font-bold mb-1">Recommended Action</div>
          <div className="text-xs text-foreground leading-relaxed">{alert.recommendation}</div>
        </div>
        <div className="bg-background/60 rounded-sm p-2 border border-border/50">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-1">Who Should Act</div>
          <div className="text-xs text-foreground">{alert.who_should_act}</div>
        </div>
      </div>

      {/* Actions */}
      {alert.status === "open" && (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => doUpdate("acknowledged")} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-amber-500/30 text-amber-400 rounded-sm hover:bg-amber-400/10 transition-colors" data-testid={`ack-alert-${alert.id}`}>
            <CheckCircle2 size={12} /> Acknowledge
          </button>
          <button onClick={() => doUpdate("actioned")} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-green-500/30 text-green-400 rounded-sm hover:bg-green-400/10 transition-colors" data-testid={`action-alert-${alert.id}`}>
            <CheckCircle2 size={12} /> Mark Actioned
          </button>
          <button onClick={() => setOverriding(!overriding)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-orange-500/30 text-orange-400 rounded-sm hover:bg-orange-400/10 transition-colors" data-testid={`override-alert-${alert.id}`}>
            <SkipForward size={12} /> Override
          </button>
          <button onClick={() => setCommenting(!commenting)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-border text-muted-foreground rounded-sm hover:bg-accent transition-colors">
            <MessageSquare size={12} /> Comment
          </button>
        </div>
      )}
      {alert.status === "acknowledged" && (
        <div className="flex gap-2">
          <button onClick={() => doUpdate("actioned")} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-green-500/30 text-green-400 rounded-sm hover:bg-green-400/10 transition-colors" data-testid={`action-alert-${alert.id}`}>
            <CheckCircle2 size={12} /> Mark Actioned
          </button>
          <button onClick={() => doUpdate("closed")} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-border text-muted-foreground rounded-sm hover:bg-accent transition-colors">
            <XCircle size={12} /> Close
          </button>
        </div>
      )}

      {overriding && (
        <div className="mt-3 space-y-2">
          <input value={overrideReason} onChange={e => setOverrideReason(e.target.value)} placeholder="Mandatory: Override justification..."
            className="w-full bg-background border border-orange-500/30 rounded-sm px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-orange-500" />
          <div className="flex gap-2">
            <button onClick={() => { doUpdate("overridden", { override_reason: overrideReason }); setOverriding(false); }} disabled={!overrideReason}
              className="text-xs px-3 py-1.5 bg-orange-500 text-white rounded-sm disabled:opacity-50 hover:bg-orange-600 transition-colors" data-testid={`confirm-override-${alert.id}`}>
              Confirm Override
            </button>
            <button onClick={() => setOverriding(false)} className="text-xs px-3 py-1.5 border border-border rounded-sm text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
        </div>
      )}

      {commenting && (
        <div className="mt-3 space-y-2">
          <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Add comment..."
            className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none h-16" />
          <div className="flex gap-2">
            <button onClick={() => { doUpdate(alert.status, { comment }); setComment(""); setCommenting(false); }} disabled={!comment}
              className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-sm disabled:opacity-50">
              Post Comment
            </button>
            <button onClick={() => setCommenting(false)} className="text-xs px-3 py-1.5 border border-border rounded-sm text-muted-foreground">Cancel</button>
          </div>
        </div>
      )}

      {alert.comments?.length > 0 && (
        <div className="mt-3 border-t border-border/50 pt-2 space-y-1">
          {alert.comments.map((c, i) => (
            <div key={i} className="text-xs text-muted-foreground">
              <span className="text-foreground font-medium">{c.by}</span>: {c.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AlertsCentre() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterStatus, setFilterStatus] = useState("open");
  const [filterDept, setFilterDept] = useState("");

  const fetchAlerts = () => {
    const params = {};
    if (filterSeverity) params.severity = filterSeverity;
    if (filterStatus) params.status = filterStatus;
    if (filterDept) params.department = filterDept;
    alertsApi.getAll(params).then(r => setAlerts(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAlerts(); }, [filterSeverity, filterStatus, filterDept]);

  const handleUpdate = async (id, data) => {
    await alertsApi.update(id, data);
    fetchAlerts();
  };

  const counts = {
    open: alerts.filter(a => a.status === "open").length,
    critical: alerts.filter(a => a.severity === "critical").length,
    acknowledged: alerts.filter(a => a.status === "acknowledged").length,
  };

  const depts = [...new Set(alerts.map(a => a.department))];

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="alerts-centre">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Module 9</div>
          <h1 className="text-2xl font-black font-heading text-foreground">Alerts & Action Centre</h1>
        </div>
        {counts.critical > 0 && (
          <div className="flex items-center gap-2 text-xs text-red-400 bg-red-400/10 border border-red-400/20 px-3 py-2 rounded-sm pulse-risk" data-testid="critical-alerts-banner">
            <Bell size={14} /> {counts.critical} CRITICAL alert{counts.critical > 1 ? "s" : ""} require immediate action
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        {[["Open Alerts", counts.open, "text-red-400"], ["Critical", counts.critical, "text-red-300"], ["Acknowledged", counts.acknowledged, "text-amber-400"]].map(([lbl, val, col]) => (
          <div key={lbl} className="bg-card border border-border rounded-sm p-3">
            <div className={`text-2xl font-mono font-medium ${col}`}>{val}</div>
            <div className="text-xs text-muted-foreground mt-1 tracking-wider uppercase">{lbl}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
          className="bg-card border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="alert-severity-filter">
          <option value="">All Severity</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="bg-card border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="alert-status-filter">
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="actioned">Actioned</option>
          <option value="overridden">Overridden</option>
          <option value="closed">Closed</option>
        </select>
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)}
          className="bg-card border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="alert-dept-filter">
          <option value="">All Departments</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        {(filterSeverity || filterStatus || filterDept) && (
          <button onClick={() => { setFilterSeverity(""); setFilterStatus(""); setFilterDept(""); }} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            <X size={12} /> Clear
          </button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{alerts.length} alert{alerts.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Alert Cards */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading alerts...</div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">No alerts match the current filters.</div>
      ) : (
        <div className="space-y-3">
          {alerts.map(alert => <AlertCard key={alert.id} alert={alert} onUpdate={handleUpdate} />)}
        </div>
      )}
    </div>
  );
}
